import React, { useState } from 'react';
import styles from '../../styles/contacts/ContactsWorkspace.module.css';
import {
  ChevronDownIcon,
  CloseIcon,
  FilterIcon,
  GridIcon,
  RowsIcon,
  SearchIcon,
} from './ContactsIcons';
import { GROUP_OPTIONS, QUICK_FILTERS, SORT_OPTIONS, SOURCE_FILTERS } from './contactsData';

const DEFAULTS = { sort: 'recent', group: 'date', source: 'any', quickFilter: 'all' };

function Select({ label, value, options, onChange }) {
  return (
    <span className={styles.selectWrap}>
      <select
        className={styles.select}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.selectChevron} aria-hidden="true">
        <ChevronDownIcon />
      </span>
    </span>
  );
}

/**
 * Sticky command bar: search, sort, grouping, source, view mode and the quick
 * filter chips. Every control is a native form element so keyboard and screen
 * reader behaviour comes for free.
 */
export default function ContactsToolbar({
  containerRef,
  searchInputRef,
  query,
  onQueryChange,
  quickFilter,
  onQuickFilterChange,
  filterCounts,
  source,
  onSourceChange,
  sort,
  onSortChange,
  group,
  onGroupChange,
  view,
  onViewChange,
  shownCount,
  totalCount,
  isFiltered,
  isLoading,
  onReset,
}) {
  // On narrow screens everything but the search collapses behind this toggle:
  // six controls stacked above the list crowd out the contacts themselves.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const adjustedCount = [
    sort !== DEFAULTS.sort,
    group !== DEFAULTS.group,
    source !== DEFAULTS.source,
    quickFilter !== DEFAULTS.quickFilter,
  ].filter(Boolean).length;

  return (
    <div ref={containerRef} className={`${styles.toolbar} ${filtersOpen ? styles.toolbarOpen : ''}`}>
      <div className={styles.toolbarTop}>
        <div className={styles.search}>
          <span className={styles.searchIcon} aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            ref={searchInputRef}
            id="contacts-search"
            type="search"
            className={styles.searchInput}
            placeholder="Search name, company, role, email…"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            autoComplete="off"
            spellCheck="false"
            aria-label="Search contacts"
          />
          <span className={styles.searchTrail}>
            {query ? (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => onQueryChange('')}
                aria-label="Clear search"
              >
                <CloseIcon size={15} />
              </button>
            ) : (
              <kbd className={styles.kbdHint}>/</kbd>
            )}
          </span>
        </div>

        <button
          type="button"
          className={`${styles.filtersToggle} ${filtersOpen ? styles.filtersToggleOn : ''}`}
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
          aria-controls="contacts-filters contacts-quick-filters"
        >
          <FilterIcon size={16} />
          Filters
          {adjustedCount > 0 ? <span className={styles.filtersCount}>{adjustedCount}</span> : null}
        </button>

        {/* display: contents on wide screens, so these sit in the row above. */}
        <div className={styles.controls} id="contacts-filters">
          <Select label="Sort contacts" value={sort} options={SORT_OPTIONS} onChange={onSortChange} />
          <Select label="Group contacts" value={group} options={GROUP_OPTIONS} onChange={onGroupChange} />
          <Select label="Filter by source" value={source} options={SOURCE_FILTERS} onChange={onSourceChange} />

          <div className={styles.viewToggle} role="group" aria-label="List layout">
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${view === 'rows' ? styles.viewToggleBtnOn : ''}`}
              onClick={() => onViewChange('rows')}
              aria-pressed={view === 'rows'}
              aria-label="List view"
              title="List view"
            >
              <RowsIcon />
            </button>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${view === 'cards' ? styles.viewToggleBtnOn : ''}`}
              onClick={() => onViewChange('cards')}
              aria-pressed={view === 'cards'}
              aria-label="Card view"
              title="Card view"
            >
              <GridIcon />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.toolbarBottom}>
        <div className={styles.chips} id="contacts-quick-filters" role="group" aria-label="Quick filters">
          {QUICK_FILTERS.map((filter) => {
            const active = quickFilter === filter.id;
            const count = filterCounts?.[filter.id];
            return (
              <button
                key={filter.id}
                type="button"
                className={`${styles.chip} ${active ? styles.chipOn : ''}`}
                onClick={() => onQuickFilterChange(filter.id)}
                aria-pressed={active}
              >
                {filter.label}
                {typeof count === 'number' ? <span className={styles.chipCount}>{count}</span> : null}
              </button>
            );
          })}
        </div>

        <div className={styles.resultLine}>
          <span aria-live="polite">
            {/* An unresolved list is not an empty one, so it does not claim a count. */}
            {isLoading
              ? 'Loading…'
              : isFiltered
              ? `${shownCount} of ${totalCount}`
              : `${totalCount} ${totalCount === 1 ? 'contact' : 'contacts'}`}
          </span>
          {isFiltered && !isLoading ? (
            <button type="button" className={styles.resetLink} onClick={onReset}>
              Reset
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
